'use client';

import React, { Suspense, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { Hourglass, CaretDown, Check, Funnel, VideoCamera } from '@phosphor-icons/react';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { Footer } from '@/components/seasons/Footer';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { FlowFooter } from '@/components/seasons/FlowFooter';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GalaxyFooter } from '@/components/seasons/GalaxyFooter';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { CometFooter } from '@/components/seasons/CometFooter';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { GravityFooter } from '@/components/seasons/GravityFooter';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { StoryhealthFooter } from '@/components/seasons/StoryhealthFooter';
import { SimpleButton } from '@/components/seasons/SimpleButton';
import { useClickOutside } from '@/lib/seasons/useClickOutside';
import { useCtrlPanelPosition } from '@/lib/seasons/useCtrlPanelPosition';
import type { WebinarSummary } from '@/lib/webinars';

// Figma Seasons gradient palette — keep in sync with [slug]/client-page.tsx
const FIGMA_GRADIENTS: Record<string, string> = {
  'winter-night':  'linear-gradient(0deg,#000a1b 0%,#053bee 100%)',
  'night-sky':     'linear-gradient(0deg,#000a1b 0%,#a0cce0 100%)',
  'ember-night':   'linear-gradient(90deg,#000a1b 0%,#eb5324 100%)',
  'dusk-glow':     'linear-gradient(0deg,#000a1b 0%,#ffcc9c 100%)',
  'deep-forest':   'linear-gradient(0deg,#000a1b 0%,#05604f 100%)',
  'moonlit-grove': 'linear-gradient(0deg,#000a1b 0%,#aad097 100%)',
  'night-bloom':   'linear-gradient(0deg,#000a1b 0%,#58364e 100%)',
  'late-frost':    'linear-gradient(0deg,#000a1b 0%,#ffadd9 100%)',
  'golden-hour':   'linear-gradient(0deg,#000a1b 0%,#ffbf00 100%)',
  'lantern-light': 'linear-gradient(0deg,#000a1b 0%,#ffe08a 100%)',
  'horizon':       'linear-gradient(0deg,#000a1b 0%,#f0f0f0 100%)',
  'open-sky':      'linear-gradient(0deg,#053bee 0%,#a0cce0 100%)',
  'summer-storm':  'linear-gradient(0deg,#053bee 0%,#eb5324 100%)',
  'dawn-break':    'linear-gradient(0deg,#053bee 0%,#ffcc9c 100%)',
  'deep-current':  'linear-gradient(0deg,#053bee 0%,#05604f 100%)',
  'spring-rain':   'linear-gradient(0deg,#053bee 0%,#aad097 100%)',
  'indigo-dusk':   'linear-gradient(0deg,#053bee 0%,#58364e 100%)',
  'april-shower':  'linear-gradient(0deg,#053bee 0%,#ffadd9 100%)',
  'equinox':       'linear-gradient(0deg,#053bee 0%,#ffbf00 100%)',
  'bright-spark':  'linear-gradient(0deg,#053bee 0%,#ffe08a 100%)',
  'first-frost':   'linear-gradient(0deg,#053bee 0%,#f0f0f0 100%)',
  'coastal':       'linear-gradient(0deg,#a0cce0 0%,#05604f 100%)',
  'fresh-air':     'linear-gradient(180deg,#a0cce0 0%,#aad097 100%)',
  'cloud-wash':    'linear-gradient(0deg,#a0cce0 0%,#f0f0f0 100%)',
  'warm-ember':    'linear-gradient(0deg,#eb5324 0%,#ffcc9c 100%)',
  'autumn-field':  'linear-gradient(0deg,#eb5324 0%,#aad097 100%)',
  'morning-dew':   'linear-gradient(90deg,#aad097 0%,#ffe08a 100%)',
  'vanilla':       'linear-gradient(0deg,#ffcc9c 0%,#f0f0f0 100%)',
};

// Only these two brands are shown publicly — same rule as /blogs and /resources.
const VISIBLE_BRANDS = new Set(['innovaccer', 'gravity']);

// Same six brands used on /blogs and /resources. Keep this in sync if a
// new sub-brand ever gets added.
const BRANDS: { value: string; label: string }[] = [
  { value: 'innovaccer', label: 'Innovaccer' },
  { value: 'gravity',    label: 'Gravity' },
];

// Same canonical topic list as /blogs — keeps the filter UX uniform.
const CANONICAL_TOPICS = [
  'Data & Interoperability',
  'AI',
  'Value-Based Care',
  'Industry & Company',
  'Population Health',
  'Patient Engagement',
  'Care Management',
  'Policy & Compliance',
  'Clinical Documentation',
  'RCM',
  'Prior Authorization',
  'Risk Adjustment',
];
const TOPIC_DISPLAY_LABEL: Record<string, string> = {
  AI: 'Artificial Intelligence',
  RCM: 'Revenue Cycle Management',
};
function topicLabel(topic: string): string {
  return TOPIC_DISPLAY_LABEL[topic] ?? topic;
}

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE_OUT } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function formatAirDate(val?: string): string {
  if (!val) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
}

function formatAirTime(val?: string): string {
  if (!val) return '';
  if (/^(\d{4})-(\d{2})-(\d{2})$/.test(val)) return ''; // date-only: no time to show
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  }) + ' ET';
}

function formatOriginallyAired(val?: string): string {
  if (!val) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
}

function MetaSep() {
  return <span className="bl-sep" aria-hidden="true" />;
}

interface Props {
  webinars: WebinarSummary[];
  /** 'flow' / 'comet' / 'galaxy' render brand nav/footer, hide the brand filter,
      scope webinars to that brand, and link cards under /<brand>/resources/webinars. */
  variant?: 'innovaccer' | 'flow' | 'comet' | 'galaxy' | 'gravity' | 'storyhealth';
}

const TABS = ['All', 'Upcoming', 'On Demand'] as const;
type Tab = typeof TABS[number];

const ONDEMAND_INITIAL = 9;
const LOAD_MORE_STEP = 6;

function WebinarsInner({ webinars, variant = 'innovaccer' }: Props) {
  const isFlow = variant === 'flow';
  const isComet = variant === 'comet';
  const isGalaxy = variant === 'galaxy';
  const isGravity = variant === 'gravity';
  const isStoryhealth = variant === 'storyhealth';
  const isBrand = isFlow || isComet || isGalaxy || isGravity || isStoryhealth;
  const allowedBrands = isFlow ? new Set(['flow']) : isComet ? new Set(['comet']) : isGalaxy ? new Set(['galaxy']) : isGravity ? new Set(['gravity']) : isStoryhealth ? new Set(['storyhealth']) : VISIBLE_BRANDS;
  const webinarHrefBase = isFlow ? '/flow/resources/webinars' : isComet ? '/comet/resources/webinars' : isGalaxy ? '/galaxy/resources/webinars' : isGravity ? '/gravity/resources/webinars' : isStoryhealth ? '/storyhealth/resources/webinars' : '/resources/webinars';
  const searchParams = useSearchParams();
  const initialBrand = searchParams?.get('brand') ?? '';
  const [filterBrand, setFilterBrand] = useState<string[]>(
    initialBrand && BRANDS.some((b) => b.value === initialBrand) ? [initialBrand] : [],
  );
  const initialTopics = useMemo(() => {
    const raw = searchParams?.get('topics') ?? '';
    return raw ? raw.split(',').filter((t) => CANONICAL_TOPICS.includes(t)) : [];
  }, [searchParams]);
  const [filterTopics, setFilterTopics] = useState<string[]>(initialTopics);
  const [searchQuery, setSearchQuery] = useState<string>(searchParams?.get('q') ?? '');
  const rawTab = searchParams?.get('tab') ?? 'All';
  const [activeTab, setActiveTab] = useState<Tab>(
    (TABS as readonly string[]).includes(rawTab) ? (rawTab as Tab) : 'All'
  );

  // Keep ?brand= URL param in sync with the dropdown — same UX as /blogs.
  function handleBrandChange(v: string[]) {
    setFilterBrand(v);
    setShownUpcoming(ONDEMAND_INITIAL);
    setShownOnDemand(ONDEMAND_INITIAL);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (v.length) url.searchParams.set('brand', v.join(','));
      else url.searchParams.delete('brand');
      window.history.replaceState({}, '', url.toString());
    }
  }

  function handleTopicsChange(v: string[]) {
    setFilterTopics(v);
    setShownUpcoming(ONDEMAND_INITIAL);
    setShownOnDemand(ONDEMAND_INITIAL);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (v.length > 0) url.searchParams.set('topics', v.join(','));
      else url.searchParams.delete('topics');
      window.history.replaceState({}, '', url.toString());
    }
  }

  function handleSearchChange(q: string) {
    setSearchQuery(q);
    setShownUpcoming(ONDEMAND_INITIAL);
    setShownOnDemand(ONDEMAND_INITIAL);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (q) url.searchParams.set('q', q);
      else url.searchParams.delete('q');
      window.history.replaceState({}, '', url.toString());
    }
  }

  function handleTabClick(tab: Tab) {
    setActiveTab(tab);
    setShownUpcoming(ONDEMAND_INITIAL);
    setShownOnDemand(ONDEMAND_INITIAL);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (tab === 'All') url.searchParams.delete('tab');
      else url.searchParams.set('tab', tab);
      window.history.replaceState({}, '', url.toString());
    }
  }

  const filtered = useMemo(() => {
    // Base filter: only the variant's visible brands
    let list = webinars.filter(w => allowedBrands.has((w.brand || 'innovaccer').toLowerCase()));
    if (filterBrand.length > 0) {
      list = list.filter((w) => filterBrand.includes((w.brand || 'innovaccer').toLowerCase()));
    }
    if (filterTopics.length > 0) {
      list = list.filter((w) => (w.topics || []).some((t) => filterTopics.includes(t)));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((w) =>
        w.title.toLowerCase().includes(q) ||
        (w.dek ?? '').toLowerCase().includes(q) ||
        (w.topics ?? []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [webinars, filterBrand, filterTopics, searchQuery]);

  const tabFiltered = useMemo(() => {
    if (activeTab === 'Upcoming') return filtered.filter((w) => w.status === 'upcoming');
    if (activeTab === 'On Demand') return filtered.filter((w) => w.status === 'on-demand');
    return filtered;
  }, [filtered, activeTab]);

  const [shownUpcoming, setShownUpcoming] = useState(ONDEMAND_INITIAL);
  const [shownOnDemand, setShownOnDemand] = useState(ONDEMAND_INITIAL);

  const isSearching = !!searchQuery.trim();
  const hasFilters = filterBrand.length > 0 || filterTopics.length > 0 || isSearching;
  const noResults = hasFilters && tabFiltered.length === 0;

  return (
    <div className="webinars-page" style={{ background: 'var(--seasons-color-surface-default)' }}>
      {isFlow
        ? <FlowNavBar />
        : isComet
        ? <CometNavBar />
        : isGalaxy
        ? <GalaxyNavBar />
        : isGravity
        ? <GravityNavBar />
        : isStoryhealth
        ? <StoryhealthNavBar />
        : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />}
        <main>
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section
          className="section-x-pad"
          style={{
            background: 'var(--seasons-color-surface-default)',
            paddingTop: 'var(--seasons-space-12)',
            paddingBottom: 'var(--seasons-space-8)',
          }}
        >
          <motion.div className="bl-page-header" initial="hidden" animate="visible" variants={stagger}>
            <motion.h1
              variants={fadeUp}
              className="type-display-xl"
              style={{ color: 'var(--seasons-color-prose-heading)', marginBottom: 0, maxWidth: 'none' }}
            >
              Webinars
            </motion.h1>
            <motion.p variants={fadeUp} className="type-body-lg bl-page-header__sub">
              Live sessions, product demos, and deep-dives from the Innovaccer team.
            </motion.p>
            {/* Search bar — sits directly under the sub-copy */}
            <motion.div variants={fadeUp} className="webinars-search webinars-search--hero">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="webinars-search__icon">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                className="webinars-search__input"
                placeholder="Search webinars"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                aria-label="Search webinars"
              />
              {searchQuery && (
                <button
                  className="webinars-search__clear"
                  onClick={() => handleSearchChange('')}
                  aria-label="Clear search"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                    <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </motion.div>
          </motion.div>
        </section>

        {/* ── Filter bar — tabs (left) + filter controls (right) ─────── */}
        <div className="bl-filter-bar section-x-pad" style={{ overflow: 'visible', paddingTop: 0, paddingBottom: 0 }}>
          <div className="bl-fb-bar">
            <div role="group" aria-label="Filter by type" className="bl-fb-v2 bl-fb-v2--bare">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`bl-fb-v2__tab${activeTab === tab ? ' bl-fb-v2__tab--active' : ''}`}
                  onClick={() => handleTabClick(tab)}
                  aria-pressed={activeTab === tab}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="bl-fb-controls">
              {!isBrand && <BrandDropdown selected={filterBrand} onChange={handleBrandChange} />}
              <FilterDropdown
                topics={CANONICAL_TOPICS}
                selected={filterTopics}
                onChange={handleTopicsChange}
              />
            </div>
          </div>
        </div>

        {/* ── Clear ──────────────────────────────────────────────────────── */}
        {noResults && (
          <section
            className="section-x-pad"
            style={{ background: 'var(--seasons-color-surface-subtle)', paddingTop: 'var(--seasons-space-16)', paddingBottom: 'var(--seasons-space-20)' }}
          >
            <NoResultsState
              onClear={() => {
                setFilterBrand([]);
                setFilterTopics([]);
                handleSearchChange('');
                if (typeof window !== 'undefined') {
                  const url = new URL(window.location.href);
                  url.searchParams.delete('brand');
                  url.searchParams.delete('topics');
                  url.searchParams.delete('q');
                  window.history.replaceState({}, '', url.toString());
                }
              }}
            />
          </section>
        )}

        {/* ── Grid ──────────────────────────────────────────────────────── */}
        {!noResults && (
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-subtle)',
              paddingTop: 'var(--seasons-space-10)',
              paddingBottom: 'var(--seasons-space-20)',
            }}
          >
            {tabFiltered.length === 0 ? (
              <EmptyState variant={activeTab === 'Upcoming' ? 'upcoming' : activeTab === 'On Demand' ? 'on-demand' : 'all'} />
            ) : activeTab === 'All' ? (
              /* ── All tab: Upcoming section + On Demand section, each with own Load More ── */
              (() => {
                const upcoming  = tabFiltered.filter((w) => w.status === 'upcoming');
                const onDemand  = tabFiltered.filter((w) => w.status !== 'upcoming');
                const upShown   = upcoming.slice(0, shownUpcoming);
                const odShown   = onDemand.slice(0, shownOnDemand);
                return (
                  <>
                    {upcoming.length > 0 && (
                      <div style={{ marginBottom: onDemand.length > 0 ? 'var(--seasons-space-16)' : 0 }}>
                        <SectionHeader heading="Upcoming" />
                        <motion.div initial="hidden" animate="visible" variants={stagger} className="webinars-grid">
                          {upShown.map((w) => (
                            <motion.div key={w.slug} variants={fadeUp}>
                              <WebinarCard webinar={w} hrefBase={webinarHrefBase} />
                            </motion.div>
                          ))}
                        </motion.div>
                        {shownUpcoming < upcoming.length && (
                          <LoadMore
                            shown={upShown.length}
                            total={upcoming.length}
                            onClick={() => setShownUpcoming((n) => n + LOAD_MORE_STEP)}
                          />
                        )}
                      </div>
                    )}
                    {onDemand.length > 0 && (
                      <>
                        <SectionHeader heading="On Demand" />
                        <motion.div initial="hidden" animate="visible" variants={stagger} className="webinars-grid">
                          {odShown.map((w) => (
                            <motion.div key={w.slug} variants={fadeUp}>
                              <WebinarCard webinar={w} hrefBase={webinarHrefBase} />
                            </motion.div>
                          ))}
                        </motion.div>
                        {shownOnDemand < onDemand.length && (
                          <LoadMore
                            shown={odShown.length}
                            total={onDemand.length}
                            onClick={() => setShownOnDemand((n) => n + LOAD_MORE_STEP)}
                          />
                        )}
                      </>
                    )}
                  </>
                );
              })()
            ) : (
              /* ── Upcoming / On Demand tab: flat grid ── */
              <>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={stagger}
                  className="webinars-grid"
                >
                  {tabFiltered.slice(0, shownOnDemand).map((w) => (
                    <motion.div key={w.slug} variants={fadeUp}>
                      <WebinarCard webinar={w} hrefBase={webinarHrefBase} />
                    </motion.div>
                  ))}
                </motion.div>
                {shownOnDemand < tabFiltered.length && (
                  <LoadMore
                    shown={Math.min(shownOnDemand, tabFiltered.length)}
                    total={tabFiltered.length}
                    onClick={() => setShownOnDemand((n) => n + LOAD_MORE_STEP)}
                  />
                )}
              </>
            )}
          </section>
        )}
      </main>

      {isFlow ? <FlowFooter /> : isComet ? <CometFooter /> : isGalaxy ? <GalaxyFooter /> : isGravity ? <GravityFooter /> : isStoryhealth ? <StoryhealthFooter /> : <Footer theme="light" />}
    </div>
  );
}

function FilterDropdown({
  topics,
  selected,
  onChange,
}: {
  topics: string[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const hasActive = selected.length > 0;
  function toggle(t: string) {
    onChange(selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);
  }
  return (
    <div className="bl-ctrl" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`bl-ctrl__trigger${hasActive ? ' bl-ctrl__trigger--active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Funnel size={13} weight={hasActive ? 'fill' : 'regular'} />
        Topics
        {hasActive && <span className="bl-ctrl__badge">{selected.length}</span>}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel" role="listbox" aria-multiselectable="true" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          {topics.map((topic) => {
            const on = selected.includes(topic);
            return (
              <button
                key={topic}
                type="button"
                className={`bl-ctrl__option${on ? ' bl-ctrl__option--selected' : ''}`}
                onClick={() => toggle(topic)}
                role="option"
                aria-selected={on}
              >
                <span className={`bl-ctrl__check${on ? ' bl-ctrl__check--on' : ''}`}>
                  {on && <Check size={10} weight="bold" color="white" />}
                </span>
                {topicLabel(topic)}
              </button>
            );
          })}
          {hasActive && (
            <div className="bl-ctrl__footer">
              <button type="button" className="bl-ctrl__clear" onClick={() => onChange([])}>
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BrandDropdown({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const hasActive = selected.length > 0;

  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  }

  return (
    <div className="bl-ctrl" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`bl-ctrl__trigger${hasActive ? ' bl-ctrl__trigger--active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        Product
        {hasActive && <span className="bl-ctrl__badge">{selected.length}</span>}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel" role="listbox" aria-multiselectable="true" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          <div className="bl-ctrl__options">
            {BRANDS.map((b) => {
              const on = selected.includes(b.value);
              return (
                <button
                  key={b.value}
                  type="button"
                  className={`bl-ctrl__option${on ? ' bl-ctrl__option--selected' : ''}`}
                  onClick={() => toggle(b.value)}
                  role="option" aria-selected={on}
                >
                  <span className={`bl-ctrl__check${on ? ' bl-ctrl__check--on' : ''}`}>{on && <Check size={10} weight="bold" color="white" />}</span>
                  {b.label}
                </button>
              );
            })}
          </div>
          <div className="bl-ctrl__footer">
            <button type="button" className="bl-ctrl__clear" onClick={() => onChange([])}>Clear all</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ heading }: { heading: string }) {
  return (
    <div className="webinars-section-header">
      <h2 className="type-display-md webinars-section-header__heading">
        {heading}
      </h2>
    </div>
  );
}

function NoResultsState({ onClear }: { onClear: () => void }) {
  return (
    <div
      style={{
        padding: 'var(--seasons-space-16) var(--seasons-space-8)',
        textAlign: 'center',
        background: 'var(--seasons-color-surface-subtle)',
        borderRadius: 'var(--seasons-radius-card)',
      }}
    >
      <h3 className="type-heading-lg" style={{ marginBottom: 'var(--seasons-space-3)' }}>
        No webinars match your filters
      </h3>
      <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', marginBottom: 'var(--seasons-space-6)' }}>
        Try adjusting or clearing your filters to find what you&apos;re looking for.
      </p>
      <button
        type="button"
        className="block-button-link"
        onClick={onClear}
        style={{ display: 'inline-flex' }}
      >
        <span className="block-button block-button--md block-button--midnight">
          <span className="block-button__text">
            <span className="btn-primary-text">Clear all filters</span>
            <span className="btn-hover-text" aria-hidden="true">Clear all filters</span>
          </span>
        </span>
      </button>
    </div>
  );
}

function LoadMore({
  shown,
  total,
  label = 'webinars',
  onClick,
}: {
  shown: number;
  total: number;
  label?: string;
  onClick: () => void;
}) {
  return (
    <div className="bl-load-more">
      <p className="bl-load-count" aria-live="polite" aria-atomic="true">
        Showing {Math.min(shown, total)} of {total} {label}
      </p>
      <SimpleButton label="Load more" size="lg" variant="outline" onClick={onClick} textSwap />
    </div>
  );
}

function WebinarCard({ webinar, hrefBase = '/resources/webinars' }: { webinar: WebinarSummary; hrefBase?: string }) {
  const isUpcoming = webinar.status === 'upcoming';
  const dateLabel = isUpcoming ? formatAirDate(webinar.airsAt) : formatOriginallyAired(webinar.originallyAired);
  const timeLabel = isUpcoming ? formatAirTime(webinar.airsAt) : '';
  const href = webinar.newsLink || `${hrefBase}/${webinar.slug}`;
  return (
    <Link href={href} className="webinar-card" {...(webinar.newsLink ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div
        className="webinar-card__media"
        style={webinar.bannerGradient && FIGMA_GRADIENTS[webinar.bannerGradient]
          ? { '--wbn-gradient': FIGMA_GRADIENTS[webinar.bannerGradient] } as React.CSSProperties
          : undefined}
      >
        {webinar.heroImg ? (
          <Image fill src={webinar.heroImg} alt="" style={{ objectFit: 'cover' }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
        ) : (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background: (webinar.bannerGradient && FIGMA_GRADIENTS[webinar.bannerGradient])
                ?? 'linear-gradient(0deg, var(--seasons-color-limestone) 0%, var(--seasons-color-cyber-blue-100) 100%)',
            }}
          />
        )}
      </div>
      <div className="webinar-card__body">
        <h3 className="type-heading-md webinar-card__title">{webinar.title}</h3>
        {webinar.dek && (
          <p className="type-body-sm webinar-card__dek">{webinar.dek}</p>
        )}
      </div>
      {/* Meta footer — pill badge + date · time · duration */}
      <div className="webinar-card__footer">
        <span className="bl-post-card__meta-text">
          {isUpcoming ? 'Live webinar' : 'On Demand webinar'}
        </span>
        {dateLabel && (
          <>
            <MetaSep />
            <span className="bl-post-card__meta-text">{dateLabel}</span>
          </>
        )}
        {timeLabel && (
          <>
            <MetaSep />
            <span className="bl-post-card__meta-text">{timeLabel}</span>
          </>
        )}
        {webinar.durationMinutes && (
          <>
            <MetaSep />
            <span className="bl-post-card__meta-text" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Hourglass size={12} weight="regular" /> {/^\d+$/.test(String(webinar.durationMinutes)) ? `${webinar.durationMinutes} min` : webinar.durationMinutes}
            </span>
          </>
        )}
      </div>
    </Link>
  );
}

/**
 * Upcoming layout: the first 2 webinars render as wide featured cards
 * (2-col grid) above the standard 3-col grid for the remaining ones.
 */
function UpcomingLayout({ webinars }: { webinars: WebinarSummary[] }) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={stagger}
      className="webinars-grid"
    >
      {webinars.map((w) => (
        <motion.div key={w.slug} variants={fadeUp}>
          <WebinarCard webinar={w} />
        </motion.div>
      ))}
    </motion.div>
  );
}

function WebinarCardFeatured({ webinar }: { webinar: WebinarSummary }) {
  const dateLabel = formatAirDate(webinar.airsAt);
  const timeLabel = formatAirTime(webinar.airsAt);
  const href = webinar.newsLink || `/resources/webinars/${webinar.slug}`;
  return (
    <Link href={href} className="webinar-card webinar-card--featured" {...(webinar.newsLink ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div
        className="webinar-card__media webinar-card__media--featured"
        style={webinar.bannerGradient && FIGMA_GRADIENTS[webinar.bannerGradient]
          ? { '--wbn-gradient': FIGMA_GRADIENTS[webinar.bannerGradient] } as React.CSSProperties
          : undefined}
      >
        {webinar.heroImg ? (
          <Image fill src={webinar.heroImg} alt="" style={{ objectFit: 'cover' }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
        ) : (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background: (webinar.bannerGradient && FIGMA_GRADIENTS[webinar.bannerGradient])
                ?? 'linear-gradient(0deg, var(--seasons-color-limestone) 0%, var(--seasons-color-cyber-blue-100) 100%)',
            }}
          />
        )}
      </div>
      <div className="webinar-card__body webinar-card__body--featured">
        <span className="prim-label">
          <VideoCamera size={10} weight="fill" aria-hidden="true" />
          Live webinar
        </span>
        <div className="webinar-card__meta">
          {dateLabel && <span className="bl-post-card__meta-text">{dateLabel}</span>}
          {timeLabel && (
            <>
              <MetaSep />
              <span className="bl-post-card__meta-text">{timeLabel}</span>
            </>
          )}
          {webinar.durationMinutes && (
            <>
              {(dateLabel || timeLabel) && <MetaSep />}
              <span className="bl-post-card__meta-text" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Hourglass size={12} weight="regular" /> {/^\d+$/.test(String(webinar.durationMinutes)) ? `${webinar.durationMinutes} min` : webinar.durationMinutes}
              </span>
            </>
          )}
        </div>
        <h3 className="type-heading-lg webinar-card__title">{webinar.title}</h3>
        {webinar.dek && (
          <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', margin: 0 }}>
            {webinar.dek}
          </p>
        )}
      </div>
    </Link>
  );
}

function EmptyState({ variant }: { variant: 'upcoming' | 'on-demand' | 'all' }) {
  const heading =
    variant === 'upcoming' ? 'No upcoming webinars right now' :
    variant === 'on-demand' ? 'No On Demand webinars yet' :
    'No webinars available';
  const body =
    variant === 'upcoming' ? 'Check back soon — new sessions are added every month.' :
    variant === 'on-demand' ? 'Recordings from past sessions will appear here.' :
    'New sessions are added regularly — check back soon.';
  return (
    <div
      style={{
        padding: 'var(--seasons-space-12) var(--seasons-space-8)',
        textAlign: 'center',
        background: 'var(--seasons-color-surface-subtle)',
        borderRadius: 'var(--seasons-radius-card)',
      }}
    >
      <h3 className="type-heading-lg" style={{ marginBottom: 'var(--seasons-space-3)' }}>{heading}</h3>
      <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)' }}>{body}</p>
    </div>
  );
}

export default function WebinarsClientPage({ webinars, variant = 'innovaccer' }: Props) {
  return (
    <Suspense fallback={null}>
      <WebinarsInner webinars={webinars} variant={variant} />
    </Suspense>
  );
}
